/* Only manual edits of a visually grouped discount line need this adapter.
   The existing keyed handlers continue to own plus, minus and Remove. */
(() => {
  if (window.__mvSolarCartQuantityBound) return;
  window.__mvSolarCartQuantityBound = true;
  let busy = false;
  let historyRefreshRunning = false;
  let historyRefreshPending = false;
  let historyRefreshFailed = false;
  let cartMutationVersion = 0;
  const root = () => window.Shopify?.routes?.root || '/';
  const read = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options,
      signal: AbortSignal.timeout(15000)
    });
    const data = await response.json();
    if (!response.ok || data.errors) throw new Error('Cart quantity update failed');
    return data;
  };

  // Checkout reads Shopify's saved cart. Never POST the visual grouped inputs:
  // a paid/free split can contain more server lines than displayed rows.
  const isCartUpdating = (drawer) => !!(drawer && (busy || historyRefreshRunning ||
    drawer.querySelector('quantity-input[data-mv-busy="true"], [data-mv-gift]:disabled, [aria-busy="true"], .cart__items--disabled')));
  const syncCheckoutStatus = () => {
    const drawer = document.querySelector('cart-drawer');
    const link = drawer?.querySelector('[data-mv-solar-checkout]');
    if (!link) return;
    const updating = isCartUpdating(drawer);
    const empty = drawer.classList.contains('is-empty');
    const label = link.querySelector('.button__label');
    const text = updating ? link.dataset.mvUpdatingLabel : historyRefreshFailed ? link.dataset.mvRecoveryLabel : link.dataset.mvReadyLabel;
    if (label && text && label.textContent !== text) label.textContent = text;
    const terms = drawer.querySelector('[id^="TNC-Checkbox-"]');
    const disabled = String(!!(updating || (!historyRefreshFailed && (empty || (terms && terms.dataset.checked !== 'true')))));
    if (link.getAttribute('aria-disabled') !== disabled) link.setAttribute('aria-disabled', disabled);
    const pending = String(updating);
    if (link.dataset.mvCheckoutPending !== pending) link.dataset.mvCheckoutPending = pending;
    // On a failed refresh, do not present the old total as ready for payment.
    [drawer.querySelector('.mv-solar-cart-summary__totals'), link.querySelector('.mv-solar-cart-footer__amount'), link.querySelector('[aria-hidden="true"]')]
      .filter(Boolean).forEach(node => { if (node.hidden !== historyRefreshFailed) node.hidden = historyRefreshFailed; });
    if (historyRefreshPending && !historyRefreshRunning && !updating) Promise.resolve().then(refreshHistoryCart);
  };
  const observeCheckoutStatus = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // Do not set aria-busy on the link: it would match our own update guard.
    new MutationObserver(syncCheckoutStatus).observe(drawer, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-mv-busy', 'data-checked', 'disabled', 'aria-busy', 'class']
    });
    syncCheckoutStatus();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeCheckoutStatus, { once: true });
  else observeCheckoutStatus();
  // Browser history can restore form values independently of server-rendered
  // cart totals. Reconcile both from Shopify on history returns only.
  async function refreshHistoryCart() {
    const drawer = document.querySelector('cart-drawer');
    if (!historyRefreshPending || historyRefreshRunning || !drawer || isCartUpdating(drawer)) return;
    historyRefreshPending = false;
    historyRefreshRunning = true;
    historyRefreshFailed = false;
    const version = cartMutationVersion;
    const controls = [...drawer.querySelectorAll('button, input, select')]
      .filter(node => !node.disabled && !node.matches('.drawer__close'));
    controls.forEach(node => { node.disabled = true; });
    syncCheckoutStatus();
    try {
      const sections = await read(root() + '?sections=cart-drawer,cart-icon-bubble', { cache: 'no-store' });
      if (version !== cartMutationVersion || drawer !== document.querySelector('cart-drawer')) {
        historyRefreshPending = true;
        return;
      }
      const parser = new DOMParser();
      const nextDrawer = parser.parseFromString(sections['cart-drawer'] || '', 'text/html').querySelector('cart-drawer');
      const nextContent = nextDrawer?.querySelector('#CartDrawer');
      const current = drawer.querySelector('#CartDrawer');
      const nextIcon = parser.parseFromString(sections['cart-icon-bubble'] || '', 'text/html').querySelector('.shopify-section');
      const icon = document.getElementById('cart-icon-bubble');
      if (!nextContent || !current || !nextIcon || !icon) throw new Error('Cart history sections incomplete');
      const focusWasInside = current.contains(document.activeElement);
      const focusedId = focusWasInside ? document.activeElement?.id : null;
      const scrollTop = drawer.querySelector('.mv-cart-scroll')?.scrollTop || 0;
      // Shrine binds close directly to this overlay in its constructor.
      // Keep that node and its listener when replacing the cart contents.
      const overlay = current.querySelector('#CartDrawer-Overlay');
      const nextOverlay = nextContent.querySelector('#CartDrawer-Overlay');
      if (overlay && nextOverlay) nextOverlay.replaceWith(overlay);
      current.replaceChildren(...nextContent.childNodes);
      icon.replaceChildren(...nextIcon.childNodes);
      drawer.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
      const scroll = drawer.querySelector('.mv-cart-scroll');
      if (scroll) scroll.scrollTop = scrollTop;
      // Preserve the drawer's existing open/closed state; do not call open().
      if (drawer.classList.contains('active')) {
        const target = (focusWasInside && document.getElementById(focusedId || '')) || drawer.querySelector('.drawer__inner');
        const container = drawer.querySelector('.drawer__inner-empty') || current;
        if (typeof trapFocus === 'function') trapFocus(container, target);
        else target?.focus({ preventScroll: true });
      }
      document.dispatchEvent(new CustomEvent('miravel:cart-updated', { detail: { source: 'history-refresh' } }));
    } catch (error) {
      if (version !== cartMutationVersion) historyRefreshPending = true;
      else {
        historyRefreshFailed = true;
        const message = drawer.querySelector('#CartDrawer-CartErrors');
        if (message) { message.textContent = drawer.dataset.mvUpdateError; message.hidden = false; message.setAttribute('role', 'alert'); }
      }
    } finally {
      controls.forEach(node => { if (node.isConnected) node.disabled = false; });
      historyRefreshRunning = false;
      syncCheckoutStatus();
    }
  }
  // Non-BFCache history loads can paint cached cart HTML before window.load.
  // Reconcile when the DOM is ready instead of waiting for slow third-party
  // resources to release pageshow. BFCache restores still use pageshow below.
  let initialHistoryRefreshRequested = false;
  const requestInitialHistoryRefresh = () => {
    if (initialHistoryRefreshRequested) return;
    const navigation = typeof performance.getEntriesByType === 'function' ? performance.getEntriesByType('navigation')[0] : null;
    if (navigation?.type !== 'back_forward') return;
    initialHistoryRefreshRequested = true;
    historyRefreshPending = true;
    refreshHistoryCart();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', requestInitialHistoryRefresh, { once: true });
  else requestInitialHistoryRefresh();
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      historyRefreshPending = true;
      refreshHistoryCart();
    } else requestInitialHistoryRefresh();
  });
  const noteCartMutation = event => {
    if (event.detail?.source === 'history-refresh') return;
    cartMutationVersion++;
    if (historyRefreshRunning) historyRefreshPending = true;
  };
  document.addEventListener('miravel:cart-updated', noteCartMutation);
  document.addEventListener('cart:updated', noteCartMutation);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches?.('form[data-bundle-form], form[data-ig-form], form[data-necklace-form], form[data-catalog-form], form[data-look-form], form[action*="/cart/add"]')) noteCartMutation(event);
  }, true);
  // Newly rendered controls can appear while an earlier GET is still pending.
  // Block their edits until reconciliation completes, without blocking close.
  ['click', 'change'].forEach(type => window.addEventListener(type, event => {
    if (!historyRefreshRunning) return;
    const control = event.target.closest?.('cart-drawer .quantity__button, cart-drawer .quantity__input, cart-drawer cart-remove-button, cart-drawer [data-mv-gift]');
    if (!control) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.type === 'change' && control.matches('.quantity__input')) control.value = control.dataset.cartQuantity || control.defaultValue;
    if (event.type === 'change' && control.matches('[data-mv-gift]')) control.checked = control.defaultChecked;
  }, true));

  const canCheckout = (drawer) => {
    if (!drawer || drawer.classList.contains('is-empty') || isCartUpdating(drawer)) return false;
    const terms = drawer.querySelector('[id^="TNC-Checkbox-"]');
    if (terms && terms.dataset.checked !== 'true') return false;
    const dirty = [...drawer.querySelectorAll('.quantity__input[data-cart-quantity]')]
      .find(input => Number(input.value) !== Number(input.dataset.cartQuantity));
    if (dirty) {
      dirty.dispatchEvent(new Event('change', { bubbles: true }));
      return false;
    }
    return true;
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-mv-solar-checkout]');
    if (!link || !document.body.classList.contains('mv-solar')) return;
    if (historyRefreshFailed && !isCartUpdating(link.closest('cart-drawer'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(root() + 'cart');
      return;
    }
    if (!canCheckout(link.closest('cart-drawer'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      syncCheckoutStatus();
    }
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.target.id !== 'CartDrawer-Form' || !document.body.classList.contains('mv-solar')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const drawer = event.target.closest('cart-drawer');
    const link = drawer?.querySelector('[data-mv-solar-checkout]');
    if (historyRefreshFailed && !isCartUpdating(drawer)) { window.location.assign(root() + 'cart'); return; }
    if (link && canCheckout(drawer)) window.location.assign(link.href);
  }, true);

  document.addEventListener('change', async (event) => {
    const input = event.target.closest?.('#shopify-section-cart-drawer .quantity__input[data-group-lines]');
    if (!input || !document.body.classList.contains('mv-solar')) return;
    let lines;
    try { lines = JSON.parse(input.dataset.groupLines); } catch { return; }
    if (!Array.isArray(lines) || lines.length < 2 ||
        lines.some(line => typeof line.key !== 'string' || !Number.isInteger(line.quantity))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const current = lines.reduce((sum, line) => sum + line.quantity, 0);
    const requested = Number(input.value);
    const drawer = input.closest('cart-drawer');
    const control = input.closest('quantity-input');
    if (busy || historyRefreshRunning || drawer.querySelector('quantity-input[data-mv-busy="true"], [data-mv-gift]:disabled')) {
      input.value = current;
      return;
    }
    if (!Number.isInteger(requested) || !input.checkValidity()) {
      input.reportValidity();
      input.value = current;
      return;
    }
    if (requested === current) return;
    const delta = requested - current;
    let endpoint, payload;
    if (delta > 0) {
      // change.js validates inventory when increasing an existing line.
      endpoint = 'cart/change.js';
      payload = { id: lines[0].key, quantity: lines[0].quantity + delta };
    } else {
      // A single update reduces only this group's keyed lines. No properties,
      // variants or unrelated personalized items are changed.
      let remove = -delta;
      const updates = {};
      [...lines].reverse().forEach(line => {
        const decrement = Math.min(line.quantity, remove);
        updates[line.key] = line.quantity - decrement;
        remove -= decrement;
      });
      endpoint = 'cart/update.js';
      payload = { updates };
    }
    payload.sections = ['cart-drawer', 'cart-icon-bubble'];
    payload.sections_url = window.location.pathname;
    const scrollTop = drawer.querySelector('.mv-cart-scroll')?.scrollTop || 0;
    const errorLabel = drawer.dataset.mvUpdateError;
    const controls = [...drawer.querySelectorAll('button, input, select')]
      .filter(node => !node.disabled && !node.matches('.drawer__close'));
    const error = drawer.querySelector('#CartDrawer-CartErrors');
    if (error) error.textContent = '';
    busy = true;
    control.dataset.mvBusy = 'true';
    controls.forEach(node => { node.disabled = true; });
    syncCheckoutStatus();
    try {
      const data = await read(root() + endpoint, { method: 'POST', body: JSON.stringify(payload) });
      if (!data.sections?.['cart-drawer'] || !data.sections?.['cart-icon-bubble']) {
        data.sections = await read(root() + '?sections=cart-drawer,cart-icon-bubble');
      }
      if (!data.sections?.['cart-drawer'] || !data.sections?.['cart-icon-bubble']) throw new Error('Cart sections unavailable');
      drawer.classList.toggle('is-empty', data.item_count === 0);
      drawer.renderContents(data, true);
      const scroll = drawer.querySelector('.mv-cart-scroll');
      if (scroll) scroll.scrollTop = scrollTop;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
    } catch {
      input.value = current;
      const message = drawer.querySelector('#CartDrawer-CartErrors');
      if (message) { message.textContent = errorLabel; message.setAttribute('role', 'alert'); }
    } finally {
      controls.forEach(node => { if (node.isConnected) node.disabled = false; });
      if (control.isConnected) control.dataset.mvBusy = 'false';
      busy = false;
      syncCheckoutStatus();
    }
  }, true);
})();
